import React from 'react';
const label = value => value.replaceAll('_', ' ');
function Lines({ title, values }) {
  return values.length ? <div><strong>{title}</strong><ul>{values.map((v, i) => <li key={i}>{v}</li>)}</ul></div> : null;
}
function Codes({ values, family = false }) {
  if (!values.length) return null;
  return <section className="resp-section">
    <h4>{family ? 'Incomplete code families — not for billing' : 'ICD-10-CM candidates — clinician and coder review required'}</h4>
    <p>No claims are submitted. Definition validity does not establish patient applicability or billability.</p>
    {values.map(v => <article key={v.id}>
      <strong>{v.code} — {v.descriptor}</strong>
      <p>{v.rationale}</p>
      <p>{v.definition_release} · {v.effective_from} through {v.effective_to}</p>
      <p>{v.encounter_date ? `Encounter date: ${v.encounter_date}` : 'Encounter date unconfirmed — do not treat this as a date-validated claim.'}</p>
      <p>Diagnosis status: {label(v.diagnosis_certainty)}. Supporting facts: {v.supporting_fact_ids.join(', ')}.</p>
      {!!v.uncertainty_ids.length && <p>Unresolved specificity: {v.uncertainty_ids.join(', ')}</p>}
      {!!v.instructions.length && <details><summary>Definition and inherited coding instructions</summary>
        <ul>{v.instructions.map((n, i) => <li key={i}>{n.origin} · {label(n.kind)}: {n.text}</li>)}</ul></details>}
      <p>Source: {v.source_id}</p>
    </article>)}
  </section>;
}

export default function ResourceConsultation({ value }) {
  if (!value) return null;
  return <>
    {!!value.orders.length && <section className="resp-section"><h4>Proposed wound-care orders</h4>
      <p>Draft care orders. No procedures have been performed or orders placed.</p>
      <ol>{value.orders.map(v => <li key={v.id}><strong>{v.wound_id} · {label(v.phase)}</strong>
        <p style={{ whiteSpace: 'pre-wrap' }}>{v.instruction}</p><p>{v.rationale}</p>
        <p>Frequency: {v.frequency}</p><p>Change or reassessment trigger: {v.change_trigger}</p>
        <p>Precautions: {v.precautions}</p><p>Responsible role: {v.responsible_role}</p>
        {!!v.product_ids.length && <p>Product options: {v.product_ids.join(', ')}</p>}
        <p>Supporting references: {v.evidence_ids.join(', ')}</p></li>)}</ol>
    </section>}
    {!!value.products.length && <section className="resp-section"><h4>Product options and source instructions</h4>
      <p>Manufacturer excerpts inform product use; they are not independent comparative-efficacy evidence.</p>
      {value.products.map(v => <article key={v.id}>
        <strong>{v.id}: {v.definition.name} · {v.definition.variant}</strong>
        <p>{v.definition.generic_class}</p><p>{v.rationale}</p>
        <Lines title="Materials" values={v.definition.materials} />
        <Lines title="Active ingredients" values={v.definition.active_ingredients} />
        <Lines title="Sizing" values={v.definition.sizing} />
        <Lines title="Secondary-layer requirements" values={v.definition.secondary_layers} />
        <Lines title="Source indications" values={v.definition.indications} />
        <Lines title="Preparation" values={v.definition.preparation} />
        <Lines title="Application" values={v.definition.application} />
        <Lines title="Change instructions" values={v.definition.change_instructions} />
        <Lines title="Contraindications" values={v.definition.contraindications} />
        <Lines title="Warnings" values={v.definition.warnings} />
        <Lines title="Compatibility" values={v.definition.compatibility} />
        {!!v.fields_not_documented.length && <p>Not documented in the supplied record: {v.fields_not_documented.map(label).join(', ')}. This does not establish absence of a restriction.</p>}
        <p>Linked reviewed actions: {v.action_ids.join(', ')}. Source: {v.source_id}.</p>
      </article>)}
    </section>}
    <Codes values={value.codes} /><Codes values={value.families} family />
    {!!value.coverage.length && <section className="resp-section"><h4>Coverage documentation worksheet</h4>
      <p>Not a coverage determination or approval guarantee. Payer criteria do not establish clinical indication.</p>
      {value.coverage.map(v => <article key={v.id}><strong>{v.policy_id} · {v.service}</strong>
        <p>{v.jurisdiction} · {v.effective_from} through {v.effective_to}</p>
        <p>Applicability: {label(v.applicability)}. {v.rationale}</p>
        <p>Selected criteria only; verify the complete applicable policy and payer requirements.</p>
        <ul>{v.criteria.map(c => <li key={c.criterion_id}><strong>{label(c.status)}</strong>
          <p>{c.criterion_text}</p><p>{c.rationale}</p>
          {!!c.fact_ids.length && <p>Supporting facts: {c.fact_ids.join(', ')}</p>}
          {!!c.uncertainty_ids.length && <p>Open decisions: {c.uncertainty_ids.join(', ')}</p>}
        </li>)}</ul><p>Source: {v.source_id}</p>
      </article>)}
    </section>}
    {!!value.gaps.length && <section className="resp-section"><h4>Resource gaps and deferred sections</h4>
      {value.gaps.map(v => <p key={v.section}><strong>{label(v.section)} · {label(v.status)}:</strong> {v.reason}</p>)}
    </section>}
  </>;
}
